import React, { useEffect, useState, useRef } from 'react';
import { Section } from './ui/Section';
import { Star, ChevronLeft, ChevronRight, Heart } from 'lucide-react';
import { Button } from './ui/Button';
interface Review {
  id: number;
  name: string;
  role: string;
  titleShort: string;
  text: string;
  tags: string[];
  image?: string;
}
const reviews: Review[] = [{
  id: 1,
  name: 'Sofía R.',
  role: 'Mamá de Martina (5)',
  titleShort: 'Sin llantos y en horario',
  text: 'Todas las mañanas eran una batalla. Mi hija de 5 años se negaba a vestirse y yo terminaba gritando. Con el kit, pegamos la rutina en la pared y ella sola empezó a seguir los pasos. Hoy salimos sin llantos y en horario. ¡Un alivio total!',
  tags: ['Mañanas', 'Menos gritos'],
  image: 'https://cdn.magicpatterns.com/uploads/bsEEwNH1DAzLJh1u1CNspg/hf_20260205_045215_1f5b77a1-88c7-4006-9448-15e6e9396ae2.png'
}, {
  id: 2,
  name: 'Laura M.',
  role: 'Mamá de Nico (7) y Dani (10)',
  titleShort: 'La paz volvió a las mañanas',
  text: "Tengo dos hijos de 7 y 10 años, y cada mañana era puro 'apúrate' y '¿te lavaste los dientes?'. Era agotador. Con las tarjetas visuales, ahora ellos mismos marcan 'listo' y saben qué sigue. Ya no tengo que repetir mil veces. La paz volvió a las mañanas.",
  tags: ['Transiciones', 'Fácil de usar']
}, {
  id: 3,
  name: 'Andrea T.',
  role: 'Mamá de Lucas (4)',
  titleShort: 'Cambio gigante en una semana',
  text: 'Mi hijo tiene 4 años y siempre luchábamos con los berrinches para ir al kinder. Desde que usamos la rutina visual y el guion de 10 segundos, las pataletas bajaron un montón. Él sabe qué sigue y yo ya no tengo que gritar. ¡Fue un cambio gigante!',
  tags: ['Imprimible', 'Menos gritos']
}];
export function ReviewsCarousel() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [cardsToShow, setCardsToShow] = useState(1);
  const [expandedCards, setExpandedCards] = useState<Set<number>>(new Set());
  // Touch handling state
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);
  const minSwipeDistance = 50;
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) {
        setCardsToShow(1);
      } else {
        setCardsToShow(2);
      }
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  const maxIndex = Math.max(0, reviews.length - cardsToShow);
  const nextSlide = () => {
    if (currentIndex < maxIndex) {
      setCurrentIndex(prev => prev + 1);
    }
  };
  const prevSlide = () => {
    if (currentIndex > 0) {
      setCurrentIndex(prev => prev - 1);
    }
  };
  const onTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };
  const onTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };
  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    if (isLeftSwipe && currentIndex < maxIndex) {
      nextSlide();
    }
    if (isRightSwipe && currentIndex > 0) {
      prevSlide();
    }
  };
  const toggleExpand = (id: number) => {
    setExpandedCards(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };
  return <Section background="white" className="relative overflow-hidden py-16 md:py-24" data-id="element-378">
      {/* Header */}
      <div className="text-center max-w-2xl mx-auto mb-12" data-id="element-379">
        <div className="inline-flex items-center px-4 py-2 rounded-full bg-gradient-to-r from-[#E6F4F4] to-[#D4EFED] text-[#4AAAA5] font-bold text-sm mb-5 shadow-sm" data-id="element-380">
          <Heart className="w-4 h-4 mr-2 fill-current" data-id="element-381" />
          Mamás reales, resultados reales
        </div>
        <h2 className="text-3xl md:text-4xl font-extrabold text-gray-800 mb-4" data-id="element-382">
          Lo que dicen otras familias
        </h2>
        <p className="text-gray-500 text-base md:text-lg" data-id="element-383">
          Historias de quienes ya transformaron sus mañanas.
        </p>
      </div>

      {/* Carousel Container */}
      <div className="relative max-w-5xl mx-auto px-4 md:px-12" data-id="element-384">
        {/* Navigation Buttons - Desktop only */}
        <button onClick={prevSlide} disabled={currentIndex === 0} className={`hidden md:flex absolute left-0 top-1/2 -translate-y-1/2 z-20 w-12 h-12 rounded-full items-center justify-center transition-all duration-300 border shadow-md ${currentIndex === 0 ? 'bg-gray-50 border-gray-200 text-gray-300 cursor-not-allowed opacity-50' : 'bg-white border-gray-100 text-[#E8845F] hover:bg-[#FFF5F0] hover:scale-110'}`} aria-label="Anterior testimonio" data-id="element-385">
          <ChevronLeft className="w-6 h-6" data-id="element-386" />
        </button>

        <button onClick={nextSlide} disabled={currentIndex === maxIndex} className={`hidden md:flex absolute right-0 top-1/2 -translate-y-1/2 z-20 w-12 h-12 rounded-full items-center justify-center transition-all duration-300 border shadow-md ${currentIndex === maxIndex ? 'bg-gray-50 border-gray-200 text-gray-300 cursor-not-allowed opacity-50' : 'bg-white border-gray-100 text-[#4AAAA5] hover:bg-[#E6F4F4] hover:scale-110'}`} aria-label="Siguiente testimonio" data-id="element-387">
          <ChevronRight className="w-6 h-6" data-id="element-388" />
        </button>

        {/* Slider Track */}
        <div className="overflow-hidden py-4 -mx-4 px-4" // Negative margin to allow shadow to show
      onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={onTouchEnd} data-id="element-389">
          <div className="flex transition-transform duration-500 ease-out" style={{
          transform: `translateX(-${currentIndex * (100 / cardsToShow)}%)`
        }} data-id="element-390">
            {reviews.map(review => {
            const isExpanded = expandedCards.has(review.id);
            const shouldTruncate = review.text.length > 140;
            const displayText = shouldTruncate && !isExpanded ? review.text.slice(0, 140) + '...' : review.text;
            return <div key={review.id} className="flex-shrink-0 px-3 md:px-4" style={{
              width: `${100 / cardsToShow}%`
            }} data-id="element-391">
                  {/* Card "Cajita" */}
                  <div className="h-full bg-white rounded-[2rem] p-7 md:p-8 shadow-lg border border-gray-100 hover:shadow-xl transition-shadow duration-300 flex flex-col relative group" data-id="element-392">
                    {/* Image if exists */}
                    {review.image && <div className="mb-5 -mx-3 -mt-3 md:-mx-4 md:-mt-4" data-id="element-393">
                        <img src={review.image} alt={`Testimonio de ${review.name}`} className="w-full h-48 object-cover rounded-t-[1.5rem]" data-id="element-394" />
                      </div>}

                    {/* Top Row: Heart & Stars */}
                    <div className="flex items-center justify-between mb-6" data-id="element-395">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#FFF5E6] to-[#FFEDD5] flex items-center justify-center shadow-sm" data-id="element-396">
                        <Heart className="w-5 h-5 text-[#E8845F] fill-current" data-id="element-397" />
                      </div>
                      <div className="flex gap-1" data-id="element-398">
                        {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 text-[#F5C842] fill-current" data-id="element-399" />)}
                      </div>
                    </div>

                    {/* Name & Role */}
                    <div className="mb-5" data-id="element-400">
                      <h4 className="font-bold text-gray-900 text-lg" data-id="element-401">
                        {review.name}
                      </h4>
                      <p className="text-gray-400 text-sm font-medium" data-id="element-402">
                        {review.role}
                      </p>
                    </div>

                    {/* Title */}
                    <h3 className="font-bold text-gray-800 text-xl mb-3 leading-tight" data-id="element-403">
                      "{review.titleShort}"
                    </h3>

                    {/* Review Text */}
                    <div className="text-gray-600 leading-relaxed text-[15px] mb-6 flex-grow" data-id="element-404">
                      <p className="inline" data-id="element-405">{displayText}</p>
                      {shouldTruncate && <button onClick={() => toggleExpand(review.id)} className="text-[#4AAAA5] font-bold ml-1 hover:text-[#3B9691] transition-colors inline-block" data-id="element-406">
                          {isExpanded ? 'Ver menos' : 'Ver más'}
                        </button>}
                    </div>

                    {/* Tags */}
                    <div className="flex flex-wrap gap-2 mb-6" data-id="element-407">
                      {review.tags.map((tag, i) => <span key={i} className="px-3 py-1.5 rounded-full bg-[#E6F4F4] text-[#4AAAA5] text-xs font-bold tracking-wide" data-id="element-408">
                          {tag}
                        </span>)}
                    </div>

                    {/* Footer */}
                    <div className="pt-5 border-t border-gray-100 mt-auto" data-id="element-409">
                      <p className="text-gray-400 text-xs font-medium" data-id="element-410">
                        Kit imprimible + Manual de Implementación (bonus)
                      </p>
                    </div>
                  </div>
                </div>;
          })}
          </div>
        </div>

        {/* Mobile swipe hint */}
        <div className="md:hidden flex items-center justify-center gap-2 mt-6 text-gray-400 text-xs font-medium animate-pulse" data-id="element-411">
          <span data-id="element-412">←</span>
          <span data-id="element-413">Desliza para ver más</span>
          <span data-id="element-414">→</span>
        </div>
      </div>

      {/* Dots Indicators */}
      <div className="flex justify-center gap-2 mt-8 md:mt-10" data-id="element-415">
        {Array.from({
        length: maxIndex + 1
      }).map((_, idx) => <button key={idx} onClick={() => setCurrentIndex(idx)} className={`h-2.5 rounded-full transition-all duration-300 ${currentIndex === idx ? 'w-8 bg-[#4AAAA5]' : 'w-2.5 bg-gray-200 hover:bg-gray-300'}`} aria-label={`Ir a la página ${idx + 1}`} data-id="element-416" />)}
      </div>

      {/* Disclaimer */}
      <p className="text-center text-gray-400 text-xs mt-8 italic max-w-md mx-auto px-4" data-id="element-417">
        *Los resultados pueden variar según la constancia. Recomendación: probar
        7–14 días.
      </p>

      {/* CTA Button */}
      <div className="text-center mt-10" data-id="element-418">
        <Button variant="secondary" size="lg" className="shadow-xl shadow-teal-200/50" onClick={() => document.getElementById('pricing')?.scrollIntoView({
        behavior: 'smooth'
      })} data-id="element-419">
          Quiero el Kit
        </Button>
      </div>
    </Section>;
}